import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { ACTIONS } from '../../hooks/usePermissions';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../common/TableDesign';

const extractItems = (response) => {
  const payload = response?.data !== undefined ? response.data : response;
  // Laravel gateway envelope: { success, data } where data is the ledger
  // envelope { success, data } where data is paginated {items,...} or array.
  const inner = payload?.data?.data !== undefined ? payload.data.data : payload?.data;
  const list = inner?.items ?? inner;
  return Array.isArray(list) ? list : [];
};

const extractPagination = (response, fallbackSize) => {
  const payload = response?.data !== undefined ? response.data : response;
  const inner = payload?.data?.data !== undefined ? payload.data.data : payload?.data;
  const total = Number(inner?.total ?? 0);
  const page = Number(inner?.page ?? inner?.current_page ?? 1);
  const perPage = Number(inner?.limit ?? inner?.per_page ?? fallbackSize);
  const lastPage = Number(inner?.totalPages ?? inner?.last_page ?? (perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1));
  return { current_page: page, last_page: lastPage, per_page: perPage, total };
};

const getErrorMessage = (error, fallback = 'Operation failed') => (
  error?.response?.data?.message || fallback
);

// Local "⋮" cell menu (mirrors Journals.js JournalRowActionsMenu) so this
// shared list page renders word-labeled actions like the rest of the ledger.
const RowActionsMenuCell = ({ items = [], alignRight = false }) => {
  const [anchor, setAnchor] = React.useState(null);
  if (!items.length) return null;
  const close = () => setAnchor(null);
  return (
    <Box textAlign={alignRight ? 'right' : 'left'}>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{ elevation: 2, sx: { minWidth: 190 } }}
      >
        {items.map((it, i) => (
          <React.Fragment key={it.label || `item-${i}`}>
            {it.dividerBefore && i > 0 && <Divider />}
            <MenuItem onClick={() => { close(); it.onClick?.(); }} sx={it.danger ? { color: 'error.main' } : undefined}>
              {it.icon && <ListItemIcon>{it.icon}</ListItemIcon>}
              {it.label}
            </MenuItem>
          </React.Fragment>
        ))}
      </Menu>
    </Box>
  );
};

/**
 * Generic ledger-backed list page.
 *
 * @param {string}   resource   RESOURCES.LEDGER.* key gating the whole page (read)
 * @param {string}   title      Page title
 * @param {string}   subtitle
 * @param {string}   addLabel   Add-button label (Add button hidden when onAdd is null)
 * @param {Array}    columns    [{ label, render(item)->node, align?, hideOnMobile? }]
 * @param {Function} fetchItems (params) => Promise<axios response>
 * @param {Function} onAdd      () => void | null
 * @param {Function} onEdit     (item) => void | null
 * @param {Function} onDelete   async (item) => void | null
 * @param {Function} deleteMessage (item) => string
 * @param {Array}    filters    [{ key, label, options: [{label,value}] }] (select, default)
 *                              | [{ type: 'picker', key, label, picker: Component, pickerProps? }]
 *                              | [{ type: 'daterange', keyFrom, keyTo, label }]
 *                              | [{ type: 'numberrange', keyFrom, keyTo, label }]
 *                              Only keys with a value are ever sent, so an endpoint that
 *                              doesn't declare a given filter in its DTO never sees it.
 * @param {Array}    rowActions [{ icon, title, label?, color?, permissionAction?, onClick(item) }]
 * @param {string}   idKey
 */
const LedgerListPage = ({
  resource,
  title,
  subtitle,
  addLabel = 'Add',
  columns = [],
  fetchItems,
  onAdd = null,
  onEdit = null,
  onDelete = null,
  deleteMessage = () => 'Are you sure you want to delete this record? This action cannot be undone.',
  filters = [],
  searchPlaceholder = 'Search...',
  emptyMessage = 'No records found.',
  idKey = 'id',
  pageSize = 15,
  extraActions = [],
  rowActions = [],
  refreshKey = 0,
  searchEnabled = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { can } = useAccessControl();
  const canRead = can(resource, ACTIONS.READ);
  const canCreate = can(resource, ACTIONS.CREATE);
  const canUpdate = can(resource, ACTIONS.UPDATE);
  const canDelete = can(resource, ACTIONS.DELETE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(pageSize);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, per_page: pageSize, total: 0 });

  const load = useCallback(async () => {
    if (!canRead) return;
    try {
      setLoading(true);
      // The ledger whitelist-validates query params: only page/limit/search
      // (+ declared filters) are accepted. Never send Laravel-style aliases
      // like per_page - the API answers 400 and the list renders empty.
      // Same rule applies per filter: only send a key when it has a value,
      // so an endpoint that doesn't declare an optional filter (e.g. no
      // amount-range support) never sees it and never 400s the whole list.
      const params = { page, limit: size };
      if (searchEnabled && searchTerm) params.search = searchTerm;
      const put = (k, v) => {
        if (v !== undefined && v !== '' && v !== null) params[k] = v;
      };
      filters.forEach((f) => {
        if (f.type === 'daterange' || f.type === 'numberrange') {
          put(f.keyFrom, filterValues[f.keyFrom]);
          put(f.keyTo, filterValues[f.keyTo]);
          return;
        }
        put(f.key, filterValues[f.key]);
      });
      const response = await fetchItems(params);
      setItems(extractItems(response));
      setPagination(extractPagination(response, size));
    } catch (error) {
      console.error(`Error fetching ${title}:`, error);
      showSnackbar(getErrorMessage(error, `Failed to fetch ${title}`), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, searchTerm, JSON.stringify(filterValues), JSON.stringify(filters.map((f) => f.key || `${f.keyFrom}:${f.keyTo}`)), canRead, refreshKey, searchEnabled]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterValues]);

  const handleDelete = (item) => {
    confirm({
      title: `Delete ${title.slice(0, -1) || 'record'}`,
      message: deleteMessage(item),
      onConfirm: async () => {
        try {
          await onDelete(item);
          showSnackbar('Deleted successfully', 'success');
          load();
        } catch (error) {
          showSnackbar(getErrorMessage(error, 'Failed to delete'), 'error');
        }
      },
    });
  };

  const visibleRowActions = (item) => {
    // rowActions defaults to [] here, but callers (e.g. CoaAccounts.js's
    // generic tab wrapper) can still pass an explicit null through - a
    // default only kicks in for undefined, so `|| []` guards that too.
    const actions = typeof rowActions === 'function' ? rowActions(item) || [] : (rowActions || []);
    return actions.filter((a) => (!a.permissionAction ? true : can(resource, a.permissionAction)));
  };

  // Word-labeled "⋮" menu — same pattern as Journals.js / CoaAccounts.js:
  // icon-only rows force a hover to learn what each button does and turn
  // into visual noise once workflow + destructive actions share a row.
  const renderRowActions = (item, forMobile = false) => {
    const menuItems = [];
    if (onEdit && canUpdate) {
      menuItems.push({ icon: <EditIcon fontSize="small" color="primary" />, label: 'Edit', onClick: () => onEdit(item) });
    }
    visibleRowActions(item).forEach((a, i) => {
      const Icon = a.icon;
      menuItems.push({
        icon: Icon ? <Icon fontSize="small" color={a.color === 'error' ? 'error' : undefined} /> : null,
        label: a.title || a.label || `Action ${i + 1}`,
        onClick: () => a.onClick(item),
        danger: a.color === 'error',
        dividerBefore: menuItems.length > 0 && a.color === 'error',
      });
    });
    if (onDelete && canDelete) {
      menuItems.push({ dividerBefore: true, icon: <DeleteIcon fontSize="small" color="error" />, label: 'Delete', danger: true, onClick: () => handleDelete(item) });
    }
    if (!menuItems.length) return null;
    return <RowActionsMenuCell items={menuItems} alignRight={forMobile} />;
  };

  if (!canRead) return <Alert severity="error">You do not have permission to view {title}.</Alert>;

  const headerActions = [
    ...extraActions.map((a, i) => (
      <Button key={`extra-${i}`} variant={a.variant || 'outlined'} startIcon={a.icon} onClick={a.onClick} fullWidth={isMobile}>
        {a.label}
      </Button>
    )),
    ...(onAdd && canCreate
      ? [
          <Button key="add" variant="contained" startIcon={<AddIcon />} onClick={onAdd} fullWidth={isMobile}>
            {addLabel}
          </Button>,
        ]
      : []),
  ];

  const mayHaveRowActions =
    (onEdit && canUpdate) ||
    (onDelete && canDelete) ||
    typeof rowActions === 'function' ||
    (rowActions || []).length > 0;
  const tableColumns = [...columns.map((c) => c.label), ...(mayHaveRowActions ? ['Actions'] : [])];

  return (
    <Box>
      <ModulePageHeader title={title} subtitle={subtitle} actions={headerActions} />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
              {searchEnabled && (
                <TextField
                  size="small"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                  sx={{ flex: '1 1 220px', minWidth: 180 }}
                />
              )}
            {filters.map((f) => {
              if (f.type === 'picker') {
                const Picker = f.picker;
                return (
                  <Box key={f.key} sx={{ flex: '0 0 auto', width: f.width || 200 }}>
                    <Picker
                      label={f.label}
                      value={filterValues[f.key] ?? ''}
                      onChange={(id) => setFilterValues((prev) => ({ ...prev, [f.key]: id }))}
                      {...(f.pickerProps || {})}
                    />
                  </Box>
                );
              }
              if (f.type === 'daterange') {
                return (
                  <Box key={`${f.keyFrom}-${f.keyTo}`} sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small" type="date" label={`${f.label} from`}
                      value={filterValues[f.keyFrom] ?? ''}
                      onChange={(e) => setFilterValues((prev) => ({ ...prev, [f.keyFrom]: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150 }}
                    />
                    <TextField
                      size="small" type="date" label={`${f.label} to`}
                      value={filterValues[f.keyTo] ?? ''}
                      onChange={(e) => setFilterValues((prev) => ({ ...prev, [f.keyTo]: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 150 }}
                    />
                  </Box>
                );
              }
              if (f.type === 'numberrange') {
                return (
                  <Box key={`${f.keyFrom}-${f.keyTo}`} sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small" type="number" label={`Min ${f.label}`}
                      value={filterValues[f.keyFrom] ?? ''}
                      onChange={(e) => setFilterValues((prev) => ({ ...prev, [f.keyFrom]: e.target.value }))}
                      sx={{ width: 130 }}
                    />
                    <TextField
                      size="small" type="number" label={`Max ${f.label}`}
                      value={filterValues[f.keyTo] ?? ''}
                      onChange={(e) => setFilterValues((prev) => ({ ...prev, [f.keyTo]: e.target.value }))}
                      sx={{ width: 130 }}
                    />
                  </Box>
                );
              }
              if (f.type === 'text' || f.type === 'number') {
                return (
                  <TextField
                    key={f.key}
                    size="small"
                    type={f.type === 'number' ? 'number' : 'text'}
                    label={f.label}
                    value={filterValues[f.key] ?? ''}
                    onChange={(e) => setFilterValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    sx={{ flex: '0 0 auto', width: f.width || 180 }}
                  />
                );
              }
              return (
                <Autocomplete
                  key={f.key}
                  size="small"
                  options={f.options || []}
                  getOptionLabel={(o) => o?.label ?? String(o?.value ?? '')}
                  value={(f.options || []).find((o) => o.value === filterValues[f.key]) || null}
                  onChange={(_, option) =>
                    setFilterValues((prev) => ({ ...prev, [f.key]: option ? option.value : '' }))
                  }
                  isOptionEqualToValue={(o, v) => o.value === v.value}
                  renderInput={(params) => <TextField {...params} label={f.label} />}
                  sx={{ flex: '0 0 auto', width: f.width || 160 }}
                  disableClearable={false}
                />
              );
            })}
            {(searchTerm || Object.values(filterValues).some((v) => v !== '' && v != null)) && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => { setSearchTerm(''); setFilterValues({}); }}
                sx={{ alignSelf: 'center', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box py={5} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : items.length === 0 ? (
            <EmptyRecordsState message={emptyMessage} />
          ) : isMobile ? (
            <ResponsiveRecordList
              items={items}
              renderItem={(item) => (
                <Stack spacing={1.25}>
                  {columns.filter((c) => !c.hideOnMobile).map((c, i) => (
                    <Box key={i} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                      <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                      <Typography variant="body2" fontWeight={i === 0 ? 700 : 400} textAlign="right">
                        {c.render ? c.render(item) : item[c.key]}
                      </Typography>
                    </Box>
                  ))}
                  {renderRowActions(item, true)}
                </Stack>
              )}
            />
          ) : (
            <DataTable
              columns={tableColumns}
              data={items}
              loading={false}
              emptyMessage={emptyMessage}
              renderRow={(item) => (
                <TableRow key={item[idKey] ?? item.code ?? item.number} hover>
                  {columns.map((c, i) => (
                    <TableCell key={i} align={c.align || 'left'}>
                      {c.render ? c.render(item) : item[c.key]}
                    </TableCell>
                  ))}
                  {mayHaveRowActions && (
                    <TableCell>{renderRowActions(item)}</TableCell>
                  )}
                </TableRow>
              )}
            />
          )}
          <TablePagination
            pagination={pagination}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => { setSize(parseInt(s, 10)); setPage(1); }}
            isMobile={isMobile}
          />
        </CardContent>
      </Card>

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
};

export default LedgerListPage;
